import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Plus,
  Trash,
  CaretUp,
  CaretDown,
  CaretRight,
  FloppyDisk,
  Warning,
  Shield,
  StackSimple,
} from '@phosphor-icons/react'
import { motion } from 'framer-motion'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { Select } from '@/components/ui/Select'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useTemplateStore } from '@/stores/templateStore'
import { usePermission } from '@/hooks/usePermission'
import { ReadOnlyBanner } from '@/pages/settings/components/SectionAccess'
import { TemplateCategory, TemplateBlock, TemplateListItem } from '@/types/template'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'

const CATEGORIES: { value: TemplateCategory; label: string }[] = [
  { value: 'cold_outreach', label: 'Cold Outreach' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'nurture', label: 'Nurture' },
  { value: 're_engage', label: 'Re-engage' },
  { value: 'introduction', label: 'Introduction' },
  { value: 'general', label: 'General' },
]

const TONE_OPTIONS = [
  { value: '', label: 'Inherit from email settings' },
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'casual', label: 'Casual' },
  { value: 'persuasive', label: 'Persuasive' },
  { value: 'urgent', label: 'Urgent' },
]

const MAX_BLOCKS = 10

// Shared input style — matches Editorial Dark token system
const FIELD_BASE =
  'flex w-full rounded-md border border-border/60 bg-background px-3 py-2 text-sm text-foreground transition-colors duration-150 placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-[#7170ff]/55 hover:border-border/80 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/[0.08] dark:bg-white/[0.02] dark:text-[#f7f8f8] dark:placeholder:text-[#62666d] dark:hover:border-white/[0.12] dark:focus-visible:border-[#7170ff]/75'

const TEXTAREA_BASE = `${FIELD_BASE} h-[96px] resize-none overflow-y-auto leading-relaxed`

const FIELD_LABEL = 'block mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground dark:text-[#8a8f98]'

interface BlockFormState {
  name: string
  instruction: string
  max_words: number | null
  tone: string
  example: string
  order: number
}

const makeEmptyBlock = (order: number): BlockFormState => ({
  name: '',
  instruction: '',
  max_words: null,
  tone: '',
  example: '',
  order,
})

const TemplateEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { fetchTemplateById, createTemplate, updateTemplate, templates, fetchTemplates } = useTemplateStore()

  const isEditMode = !!id
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isSystemTemplate, setIsSystemTemplate] = useState(false)
  // Two independent reasons this form can be read-only: the template is a
  // system preset (nobody edits those), or the ROLE cannot write templates --
  // Analyst holds mail_templates.manage = 'view' (AB#22340). Everything the
  // form disables keys off the union; only the "System" badge and its notice
  // stay specific to the preset case.
  const { canWrite } = usePermission('mail_templates', 'manage')
  const readOnly = isSystemTemplate || !canWrite

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState<TemplateCategory>('general')
  const [subjectInstruction, setSubjectInstruction] = useState('')
  const [blocks, setBlocks] = useState<BlockFormState[]>([makeEmptyBlock(0)])

  const [expandedBlocks, setExpandedBlocks] = useState<Set<number>>(new Set([0]))
  const [advancedOpen, setAdvancedOpen] = useState<Set<number>>(new Set())
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)

  const [presetId, setPresetId] = useState<string>('')

  const initialSnapshotRef = useRef<string>('')

  const currentSnapshot = useMemo(
    () => JSON.stringify({ name, description, category, subjectInstruction, blocks }),
    [name, description, category, subjectInstruction, blocks],
  )

  const isDirty = initialSnapshotRef.current !== '' && currentSnapshot !== initialSnapshotRef.current

  useEffect(() => {
    if (templates.length === 0) {
      fetchTemplates()
    }
  }, [templates.length, fetchTemplates])

  useEffect(() => {
    if (isEditMode && id) {
      setIsLoading(true)
      fetchTemplateById(Number(id))
        .then((template) => {
          const loadedBlocks: BlockFormState[] = template.blocks.map((b: TemplateBlock) => ({
            name: b.name,
            instruction: b.instruction,
            max_words: b.max_words ?? null,
            tone: b.tone || '',
            example: b.example || '',
            order: b.order,
          }))
          setName(template.name)
          setDescription(template.description || '')
          setCategory(template.category)
          setSubjectInstruction(template.subject_instruction || '')
          setIsSystemTemplate(template.is_system)
          setBlocks(loadedBlocks)
          setExpandedBlocks(new Set([0]))
          initialSnapshotRef.current = JSON.stringify({
            name: template.name,
            description: template.description || '',
            category: template.category,
            subjectInstruction: template.subject_instruction || '',
            blocks: loadedBlocks,
          })
        })
        .catch(() => {
          toast.error('Failed to load template')
          navigate('/templates')
        })
        .finally(() => setIsLoading(false))
    } else {
      initialSnapshotRef.current = JSON.stringify({
        name: '',
        description: '',
        category: 'general' as TemplateCategory,
        subjectInstruction: '',
        blocks: [makeEmptyBlock(0)],
      })
    }
  }, [isEditMode, id, fetchTemplateById, navigate])

  const handlePresetChange = async (selectedId: string) => {
    setPresetId(selectedId)
    if (!selectedId) return

    try {
      const template = await fetchTemplateById(Number(selectedId))
      setBlocks(
        template.blocks.map((b: TemplateBlock) => ({
          name: b.name,
          instruction: b.instruction,
          max_words: b.max_words ?? null,
          tone: b.tone || '',
          example: b.example || '',
          order: b.order,
        }))
      )
      setExpandedBlocks(new Set([0]))
      toast.success('Blocks loaded from preset')
    } catch {
      toast.error('Failed to load preset')
    }
  }

  const updateBlock = (index: number, field: keyof BlockFormState, value: string | number | null) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)))
  }

  const addBlock = () => {
    if (blocks.length >= MAX_BLOCKS) return
    const newBlock = makeEmptyBlock(blocks.length)
    setBlocks((prev) => [...prev, newBlock])
    setExpandedBlocks((prev) => new Set(prev).add(blocks.length))
  }

  const removeBlock = (index: number) => {
    if (blocks.length <= 1) return
    setBlocks((prev) => prev.filter((_, i) => i !== index).map((b, i) => ({ ...b, order: i })))
    setDeleteConfirmIndex(null)
    setExpandedBlocks((prev) => {
      const next = new Set<number>()
      prev.forEach((i) => {
        if (i < index) next.add(i)
        else if (i > index) next.add(i - 1)
      })
      return next
    })
  }

  const moveBlock = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= blocks.length) return
    setBlocks((prev) => {
      const next = [...prev]
      const temp = next[index]
      next[index] = next[newIndex]
      next[newIndex] = temp
      return next.map((b, i) => ({ ...b, order: i }))
    })
  }

  const toggleExpanded = (index: number) => {
    setExpandedBlocks((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const toggleAdvanced = (index: number) => {
    setAdvancedOpen((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Template name is required')
      return
    }
    if (blocks.some((b) => !b.name.trim() || !b.instruction.trim())) {
      toast.error('All blocks must have a name and instruction')
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        subject_instruction: subjectInstruction.trim() || undefined,
        blocks: blocks.map((b, i) => ({
          name: b.name.trim(),
          instruction: b.instruction.trim(),
          max_words: b.max_words || undefined,
          tone: b.tone || undefined,
          example: b.example.trim() || undefined,
          order: i,
        })),
      }

      if (isEditMode && id) {
        await updateTemplate(Number(id), payload)
      } else {
        await createTemplate(payload)
      }
      initialSnapshotRef.current = currentSnapshot
      navigate('/templates')
    } catch {
      toast.error('Failed to save template')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExit = () => {
    if (isDirty && !readOnly) {
      setShowDiscardConfirm(true)
    } else {
      navigate('/templates')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const systemPresets = templates.filter((t: TemplateListItem) => t.is_system)

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExit}
          className="text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04]"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" weight="regular" />
          Back
        </Button>
        <div className="flex items-center gap-2.5 min-w-0">
          <h1
            className="text-foreground dark:text-[#f7f8f8] truncate"
            style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
          >
            {isEditMode ? (readOnly ? 'View template' : 'Edit template') : 'Create template'}
          </h1>
          {isSystemTemplate && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-[#5e6ad2]/[0.12] dark:border dark:border-[#5e6ad2]/25 dark:text-[#a5b4fc]"
              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              <Shield className="h-3 w-3" weight="regular" />
              System
            </span>
          )}
        </div>
      </div>

      {/* Role read-only notice. Distinct from the system-preset notice below:
          there is nothing to duplicate your way out of here — the role simply
          does not write templates (AB#22340). */}
      {!canWrite && !isSystemTemplate && (
        <ReadOnlyBanner>
          You can read this template and how its blocks are structured, but your
          role cannot create, edit or delete templates.
        </ReadOnlyBanner>
      )}

      {/* System template notice — quiet card chrome to match the rest of the
          page; amber lives only in the icon as a semantic indicator. */}
      {isSystemTemplate && (
        <div className="flex items-start gap-3 rounded-lg border border-border/50 bg-card px-3.5 py-3 dark:border-white/[0.08] dark:bg-white/[0.02]">
          <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md bg-amber-500/10 dark:bg-amber-500/[0.12]">
            <Warning className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" weight="regular" />
          </div>
          <div className="space-y-0.5">
            <p
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
            >
              Read-only system template
            </p>
            <p
              className="text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
            >
              Duplicate this template to create an editable version.
            </p>
          </div>
        </div>
      )}

      {/* Details */}
      <section className="space-y-3">
        <h2
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.182px' }}
        >
          Details
        </h2>
        <Card
          variant="default"
          className="rounded-lg border-border/50 dark:bg-white/[0.02] dark:border-white/[0.08]"
        >
          <CardContent className="p-5 space-y-5">
            <div>
              <label className={FIELD_LABEL}>Name</label>
              <Input
                placeholder="e.g. Cold Outreach — Professional"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div>
              <label className={FIELD_LABEL}>Description</label>
              <textarea
                className={TEXTAREA_BASE}
                placeholder="Describe this template's purpose…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={readOnly}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={FIELD_LABEL}>Category</label>
                <Select
                  value={category}
                  onChange={(v) => setCategory(v as TemplateCategory)}
                  options={CATEGORIES}
                  disabled={readOnly}
                  ariaLabel="Category"
                  className="w-full block"
                />
              </div>
              <div className="hidden sm:flex items-end">
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
                  style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                >
                  Set the default from the templates list.
                </p>
              </div>
            </div>

            <div>
              <label className={FIELD_LABEL}>Subject instruction</label>
              <textarea
                className={`${TEXTAREA_BASE} !h-[72px]`}
                placeholder="Instructions for generating the email subject line…"
                value={subjectInstruction}
                onChange={(e) => setSubjectInstruction(e.target.value)}
                disabled={readOnly}
              />
            </div>

            {!isEditMode && systemPresets.length > 0 && (
              <div>
                <label className={FIELD_LABEL}>Start from preset</label>
                <Select
                  value={presetId}
                  onChange={(v) => handlePresetChange(v)}
                  options={[
                    { value: '', label: 'Select a preset…' },
                    ...systemPresets.map((t: TemplateListItem) => ({ value: String(t.id), label: t.name })),
                  ]}
                  placeholder="Select a preset…"
                  ariaLabel="Preset"
                  className="w-full block"
                />
                <p
                  className="mt-1.5 text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                >
                  Loads blocks from a system template as a starting point.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Blocks */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2
              className="text-foreground dark:text-[#f7f8f8]"
              style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.182px' }}
            >
              Blocks
            </h2>
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/60 text-muted-foreground dark:bg-white/[0.04] dark:border dark:border-white/[0.06] dark:text-[#8a8f98]"
              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
            >
              <StackSimple className="h-3 w-3" weight="regular" />
              {blocks.length}/{MAX_BLOCKS}
            </span>
          </div>
        </div>

        <div className="space-y-2.5">
          {blocks.map((block, index) => {
            const isExpanded = expandedBlocks.has(index)
            const isAdvancedOpen = advancedOpen.has(index)

            return (
              <motion.div
                key={index}
                layout
                transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                <Card
                  variant="default"
                  padding="none"
                  className="rounded-lg border-border/50 transition-colors duration-150 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:hover:bg-white/[0.03] dark:hover:border-[#7170ff]/20"
                >
                  {/* Header */}
                  <button
                    type="button"
                    className="w-full flex items-center gap-2.5 p-3.5 text-left transition-colors duration-150 hover:bg-muted/40 dark:hover:bg-white/[0.02] rounded-t-lg"
                    onClick={() => toggleExpanded(index)}
                  >
                    <CaretRight
                      className={cn(
                        'h-3.5 w-3.5 text-muted-foreground/70 transition-transform duration-150 dark:text-[#8a8f98]',
                        isExpanded && 'rotate-90',
                      )}
                      weight="regular"
                    />
                    <span
                      className="text-muted-foreground/50 dark:text-[#62666d] tabular-nums"
                      style={{ fontSize: '12px', fontWeight: 510 }}
                    >
                      #{index + 1}
                    </span>
                    <span
                      className="flex-1 truncate text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      {block.name || <span className="text-muted-foreground/60 dark:text-[#62666d] italic font-normal">Untitled block</span>}
                    </span>

                    <span
                      className="flex items-center gap-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveBlock(index, 'up')}
                        disabled={index === 0 || readOnly}
                        aria-label="Move up"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                      >
                        <CaretUp className="h-3.5 w-3.5" weight="regular" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveBlock(index, 'down')}
                        disabled={index === blocks.length - 1 || readOnly}
                        aria-label="Move down"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05]"
                      >
                        <CaretDown className="h-3.5 w-3.5" weight="regular" />
                      </Button>
                    </span>
                  </button>

                  {/* Body */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-4 border-t border-border/50 dark:border-white/[0.06] pt-4">
                      <div>
                        <label className={FIELD_LABEL}>Block name</label>
                        <Input
                          placeholder="e.g. Opening Hook"
                          value={block.name}
                          onChange={(e) => updateBlock(index, 'name', e.target.value)}
                          disabled={readOnly}
                        />
                      </div>

                      <div>
                        <label className={FIELD_LABEL}>Instruction</label>
                        <textarea
                          className={TEXTAREA_BASE}
                          placeholder="Describe what this block should contain…"
                          value={block.instruction}
                          onChange={(e) => updateBlock(index, 'instruction', e.target.value)}
                          disabled={readOnly}
                        />
                      </div>

                      <div>
                        <button
                          type="button"
                          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors duration-150 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8]"
                          onClick={() => toggleAdvanced(index)}
                          style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                        >
                          <CaretRight
                            className={cn(
                              'h-3 w-3 transition-transform duration-150',
                              isAdvancedOpen && 'rotate-90',
                            )}
                            weight="regular"
                          />
                          Advanced
                        </button>

                        {isAdvancedOpen && (
                          <div className="mt-3 space-y-4 pl-4 border-l-2 border-border/50 dark:border-white/[0.06]">
                            <div>
                              <label className={FIELD_LABEL}>Max words</label>
                              <input
                                type="number"
                                min={1}
                                placeholder="No limit"
                                value={block.max_words ?? ''}
                                onChange={(e) =>
                                  updateBlock(index, 'max_words', e.target.value ? Number(e.target.value) : null)
                                }
                                disabled={readOnly}
                                className={`${FIELD_BASE} h-9 w-32`}
                              />
                            </div>

                            <div>
                              <label className={FIELD_LABEL}>Tone</label>
                              <Select
                                value={block.tone}
                                onChange={(v) => updateBlock(index, 'tone', v)}
                                options={TONE_OPTIONS}
                                disabled={readOnly}
                                ariaLabel="Tone"
                                className="w-full block"
                              />
                            </div>

                            <div>
                              <label className={FIELD_LABEL}>Example</label>
                              <textarea
                                className={`${TEXTAREA_BASE} !h-[72px]`}
                                placeholder="An example of what this block should look like…"
                                value={block.example}
                                onChange={(e) => updateBlock(index, 'example', e.target.value)}
                                disabled={readOnly}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      {!readOnly && (
                        <div className="pt-3 border-t border-border/50 dark:border-white/[0.06]">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive dark:text-[#8a8f98] dark:hover:text-red-400 dark:hover:bg-red-500/[0.06]"
                            onClick={() => setDeleteConfirmIndex(index)}
                            disabled={blocks.length <= 1}
                            title={blocks.length <= 1 ? 'At least one block is required' : undefined}
                          >
                            <Trash className="h-3.5 w-3.5 mr-1.5" weight="regular" />
                            Remove block
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </motion.div>
            )
          })}
        </div>

        {!readOnly && (
          <Button
            variant="outline"
            onClick={addBlock}
            disabled={blocks.length >= MAX_BLOCKS}
            title={blocks.length >= MAX_BLOCKS ? 'Maximum 10 blocks' : undefined}
            className="w-full rounded-lg border border-dashed border-border/60 bg-transparent text-muted-foreground hover:border-[#7170ff]/40 hover:text-foreground hover:bg-muted/30 dark:border-white/[0.10] dark:text-[#8a8f98] dark:hover:border-[#7170ff]/30 dark:hover:bg-white/[0.03] dark:hover:text-[#f7f8f8]"
          >
            <Plus className="h-4 w-4 mr-2" weight="regular" />
            Add block
            {blocks.length >= MAX_BLOCKS && (
              <span
                className="ml-2 text-muted-foreground/70 dark:text-[#62666d]"
                style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
              >
                (max reached)
              </span>
            )}
          </Button>
        )}
      </section>

      {/* Sticky action bar — sits inside the page container so it aligns with
          the form content (left edge + max-w-3xl). Negative side margins +
          matching padding extend the visual chrome past the content edges so
          the border and blurred bg feel like a contained footer for this
          section, not a viewport-wide bar. */}
      <div className="sticky bottom-0 z-30 -mx-4 mt-2 border-t border-border/60 bg-background/85 backdrop-blur-md dark:border-white/[0.06] dark:bg-[#0f1011]/85">
        <div className="px-4 py-3 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleExit}
            className="dark:border-white/[0.10] dark:bg-transparent dark:text-[#d0d6e0] dark:hover:bg-white/[0.04]"
          >
            {readOnly ? 'Back' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button
              variant="default"
              onClick={handleSave}
              loading={isSaving}
              icon={<FloppyDisk className="h-4 w-4" weight="regular" />}
            >
              {isEditMode ? 'Save changes' : 'Create template'}
            </Button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={deleteConfirmIndex !== null}
        title="Remove block"
        description="Are you sure you want to remove this block? This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={() => deleteConfirmIndex !== null && removeBlock(deleteConfirmIndex)}
        onCancel={() => setDeleteConfirmIndex(null)}
      />

      <ConfirmDialog
        open={showDiscardConfirm}
        title="Discard changes?"
        description="You have unsaved changes. Leaving now will discard them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        tone="destructive"
        onConfirm={() => {
          setShowDiscardConfirm(false)
          navigate('/templates')
        }}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </div>
  )
}

export default TemplateEditorPage
